import { ProductSelection } from '@/components/products/CheckoutHandlerContext';
import { withQueryParam } from '@/shared/destinationURL';

/**
 * The `sf` parameter: enough to put the visitor back where they were after a same-tab checkout
 * redirect, and deliberately no more.
 *
 * The popup is the normal path, but when a browser blocks it the page navigates to Stripe and the
 * modal dies with it. On the way back this restores what they had picked.
 *
 * **This value is attacker-controllable.** It arrives on a URL, so anyone can craft one and send it
 * to someone. Three things keep that harmless:
 *
 * 1. It carries only opaque ids — never an email, a token, a price, or anything the server would
 *    trust. A forged one can name a different product, nothing more.
 * 2. Every field is validated against a strict shape here. Anything unexpected is discarded whole
 *    rather than partially applied.
 * 3. Restoring it never starts a payment. It reopens the flow with the choice pre-filled, and the
 *    visitor still has to press the button — so a crafted link cannot make anyone buy anything,
 *    and the server re-checks eligibility and price when they do.
 */

/** Mongo ObjectIds and Stripe price ids: hex or `price_`-prefixed alphanumerics. */
const ID = /^[a-zA-Z0-9_]{1,64}$/;
const isId = (value: unknown): value is string => typeof value === 'string' && ID.test(value);

export const RESUME_PARAM = 'sf';

export const serializeResumeState = (selection: ProductSelection): string =>
  encodeURIComponent(
    JSON.stringify({
      p: selection.productId,
      r: selection.priceId,
      h: selection.associatedHabitatIds,
      o: selection.offerId,
    }),
  );

/** Parse and validate. Returns undefined for anything that is not exactly the expected shape. */
export const parseResumeState = (raw: string | null | undefined): ProductSelection | undefined => {
  if (!raw) {
    return undefined;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(decodeURIComponent(raw));
  } catch {
    return undefined;
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return undefined;
  }
  const { p, r, h, o } = parsed as Record<string, unknown>;
  if (!isId(p) || !isId(r)) {
    return undefined;
  }
  // A habitat list must be a short array of ids or absent; anything else discards the whole thing
  // rather than being silently trimmed.
  let habitats: string[] | undefined;
  if (h !== undefined) {
    if (!Array.isArray(h) || h.length > 8 || !h.every(isId)) {
      return undefined;
    }
    habitats = h as string[];
  }
  if (o !== undefined && !isId(o)) {
    return undefined;
  }
  return { productId: p, priceId: r, associatedHabitatIds: habitats, offerId: o as string | undefined };
};

/** Add the resume payload to a return path, preserving any query and fragment it already has. */
export const withResumeState = (path: string, selection: ProductSelection) =>
  withQueryParam(path, RESUME_PARAM, serializeResumeState(selection));
